import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Bell, Mail, MessageSquare, Webhook, Shield, Clock,
  CheckCircle, XCircle, AlertTriangle, TrendingUp,
  Send, Settings, BarChart3, Users, Zap, Filter
} from 'lucide-react';

import { authFetch, API_URL } from '../utils/authFetch';

const MultiChannelAlerts = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [routingRules, setRoutingRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [configForm, setConfigForm] = useState({
    user_id: 'admin',
    email: '',
    phone: '',
    slack_webhook: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, historyRes, rulesRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/alerts/stats`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/alerts/history?limit=20`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/alerts/routing-rules`).then(r => r.json())
      ]);
      setStats(statsRes);
      setHistory(historyRes);
      setRoutingRules(rulesRes);
    } catch (err) {
      // Silently handle fetch errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSendTestAlert = async () => {
    setSendingTest(true);
    try {
      await authFetch(`${API_URL}/api/securesphere/alerts/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Test Alert',
          message: 'This is a test alert from SecureSphere',
          severity: 'medium',
          category: 'security',
          source: 'manual_test'
        })
      });
      fetchData();
    } catch (err) {
      // Handle silently
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await authFetch(`${API_URL}/api/securesphere/alerts/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configForm)
      });
    } catch (err) {
      // Handle silently
    }
  };

  const channelIcons = useMemo(() => ({
    push: Bell,
    email: Mail,
    sms: MessageSquare,
    slack: Zap,
    webhook: Webhook
  }), []);

  const severityColors = useMemo(() => ({
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    info: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30'
  }), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="alerts-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="multi-channel-alerts">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Multi-Channel Alerts</h2>
          <p className="text-neutral-400">Firebase Push, SendGrid Email, Twilio SMS & Webhook Notifications</p>
        </div>
        <Button
          data-testid="send-test-alert-btn"
          onClick={handleSendTestAlert}
          disabled={sendingTest}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Send className="w-4 h-4 mr-2" />
          {sendingTest ? 'Sending...' : 'Send Test Alert'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="routing">Routing Rules</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {stats && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className="w-4 h-4 text-orange-400" />
                      <span className="text-xs text-neutral-400">Total (24h)</span>
                    </div>
                    <p className="text-2xl font-bold text-white" data-testid="alerts-total-24h">{stats.summary.total_alerts_24h}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-neutral-400">Critical</span>
                    </div>
                    <p className="text-2xl font-bold text-red-400">{stats.summary.critical}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-neutral-400">Acknowledged</span>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{stats.summary.acknowledged}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      <span className="text-xs text-neutral-400">Pending</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-400">{stats.summary.pending}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Channel Performance & Category */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-orange-400" />
                      Channel Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(stats.by_channel).map(([channel, data]) => {
                        const Icon = channelIcons[channel] || Bell;
                        const deliveryRate = data.sent > 0 ? ((data.delivered / data.sent) * 100).toFixed(1) : 0;
                        return (
                          <div key={channel} className="bg-[#1a1a1a] rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-orange-400" />
                                <span className="text-sm text-white capitalize">{channel}</span>
                              </div>
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                {deliveryRate}%
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-neutral-400">
                              <span>Sent: {data.sent}</span>
                              <span>Delivered: {data.delivered}</span>
                              <span className="text-red-400">Failed: {data.failed}</span>
                            </div>
                            <div className="mt-2 h-1.5 bg-black/30 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                                style={{ width: `${deliveryRate}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-orange-400" />
                      Alerts by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stats.by_category).map(([cat, count]) => {
                        const maxCount = Math.max(...Object.values(stats.by_category));
                        const percent = maxCount > 0 ? (count / maxCount) * 100 : 0;
                        return (
                          <div key={cat} className="flex items-center justify-between">
                            <span className="text-sm text-neutral-300 capitalize">{cat}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-black/30 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="text-sm text-white w-8 text-right">{count}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Response Times */}
                    <div className="mt-6 pt-4 border-t border-white/10">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        Response Times
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#1a1a1a] rounded p-2">
                          <p className="text-xs text-neutral-500">Avg Ack</p>
                          <p className="text-lg font-bold text-white">{stats.response_times.avg_acknowledgment_minutes}m</p>
                        </div>
                        <div className="bg-[#1a1a1a] rounded p-2">
                          <p className="text-xs text-neutral-500">Avg Resolution</p>
                          <p className="text-lg font-bold text-white">{stats.response_times.avg_resolution_minutes}m</p>
                        </div>
                        <div className="bg-[#1a1a1a] rounded p-2">
                          <p className="text-xs text-neutral-500">P95 Ack</p>
                          <p className="text-lg font-bold text-orange-400">{stats.response_times.p95_acknowledgment_minutes}m</p>
                        </div>
                        <div className="bg-[#1a1a1a] rounded p-2">
                          <p className="text-xs text-neutral-500">Escalation Rate</p>
                          <p className="text-lg font-bold text-yellow-400">{stats.escalations.escalation_rate_percent}%</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Channels Tab */}
        <TabsContent value="channels" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Firebase Push', icon: Bell, desc: 'Real-time push notifications to mobile & web', status: 'active', color: 'from-orange-500/20 to-amber-500/10', borderColor: 'border-orange-500/30' },
              { name: 'SendGrid Email', icon: Mail, desc: 'Rich HTML email alerts with templates', status: 'active', color: 'from-blue-500/20 to-cyan-500/10', borderColor: 'border-blue-500/30' },
              { name: 'Twilio SMS', icon: MessageSquare, desc: 'Critical alerts via SMS to any phone', status: 'active', color: 'from-red-500/20 to-pink-500/10', borderColor: 'border-red-500/30' },
              { name: 'Slack', icon: Zap, desc: 'Channel alerts in Slack workspaces', status: 'active', color: 'from-purple-500/20 to-violet-500/10', borderColor: 'border-purple-500/30' },
              { name: 'Webhooks', icon: Webhook, desc: 'Custom HTTP webhooks for integrations', status: 'active', color: 'from-green-500/20 to-emerald-500/10', borderColor: 'border-green-500/30' },
              { name: 'PagerDuty', icon: AlertTriangle, desc: 'Incident management escalations', status: 'active', color: 'from-yellow-500/20 to-amber-500/10', borderColor: 'border-yellow-500/30' }
            ].map((ch) => (
              <Card key={ch.name} className={`bg-gradient-to-br ${ch.color} ${ch.borderColor}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <ch.icon className="w-8 h-8 text-white/80" />
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      {ch.status}
                    </Badge>
                  </div>
                  <h3 className="text-white font-semibold mb-1">{ch.name}</h3>
                  <p className="text-sm text-neutral-400">{ch.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Severity Routing */}
          <Card className="bg-[#242424] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Severity-Based Channel Routing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { severity: 'Critical', channels: ['Push', 'SMS', 'Email', 'PagerDuty'], color: 'bg-red-500' },
                  { severity: 'High', channels: ['Push', 'Email', 'Slack'], color: 'bg-orange-500' },
                  { severity: 'Medium', channels: ['Email', 'Slack'], color: 'bg-yellow-500' },
                  { severity: 'Low', channels: ['Email'], color: 'bg-blue-500' },
                ].map((rule) => (
                  <div key={rule.severity} className="flex items-center gap-4 bg-[#1a1a1a] rounded-lg p-3">
                    <Badge className={`${rule.color} text-white min-w-[80px] justify-center`}>
                      {rule.severity}
                    </Badge>
                    <div className="flex items-center gap-2 flex-wrap">
                      {rule.channels.map((ch) => (
                        <Badge key={ch} variant="outline" className="text-neutral-300 border-white/20">
                          {ch}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Routing Rules Tab */}
        <TabsContent value="routing" className="space-y-6">
          {routingRules && (
            <div className="space-y-4">
              {routingRules.rules.map((rule, i) => (
                <Card key={rule.id || rule.name || i} className="bg-[#242424] border-white/10">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-white font-semibold">{rule.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-neutral-500">Condition:</span>
                          {rule.condition.severity && (
                            <Badge variant="outline" className="text-orange-400 border-orange-500/30 text-xs">
                              {Array.isArray(rule.condition.severity) ? rule.condition.severity.join(', ') : rule.condition.severity}
                            </Badge>
                          )}
                          {rule.condition.category && (
                            <Badge variant="outline" className="text-blue-400 border-blue-500/30 text-xs">
                              {rule.condition.category}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge className={rule.escalation.enabled ? 'bg-orange-500/20 text-orange-400' : 'bg-neutral-500/20 text-neutral-400'}>
                        {rule.escalation.enabled ? `Escalation: ${rule.escalation.delay_minutes}m` : 'No Escalation'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Filter className="w-3 h-3 text-neutral-500" />
                      {rule.channels.map((ch) => (
                        <Badge key={ch} variant="outline" className="text-green-400 border-green-500/30 capitalize">
                          {ch}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          {history && history.alerts && history.alerts.length > 0 ? (
            <div className="space-y-3">
              {history.alerts.map((alert, i) => (
                <Card key={alert.id || alert.name || i} className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={severityColors[alert.severity] || severityColors.info}>
                            {alert.severity}
                          </Badge>
                          <h4 className="text-white font-semibold">{alert.title}</h4>
                        </div>
                        <p className="text-sm text-neutral-400">{alert.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500">
                          <span>Source: {alert.source}</span>
                          <span>Category: {alert.category}</span>
                          <span>Deliveries: {alert.deliveries?.length || 0}</span>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-neutral-300 border-white/20 capitalize">
                        {alert.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-[#242424] border-white/10">
              <CardContent className="p-8 text-center">
                <Bell className="w-12 h-12 text-neutral-600 mx-auto mb-3" />
                <p className="text-neutral-400">No alert history yet. Send a test alert to get started.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config" className="space-y-6">
          <Card className="bg-[#242424] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Alert Channel Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-w-lg">
                <div>
                  <Label className="text-neutral-300">Email (SendGrid)</Label>
                  <Input
                    data-testid="config-email-input"
                    type="email"
                    placeholder="alerts@company.com"
                    value={configForm.email}
                    onChange={(e) => setConfigForm({...configForm, email: e.target.value})}
                    className="bg-[#1a1a1a] border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-neutral-300">Phone (Twilio SMS)</Label>
                  <Input
                    data-testid="config-phone-input"
                    type="tel"
                    placeholder="+1234567890"
                    value={configForm.phone}
                    onChange={(e) => setConfigForm({...configForm, phone: e.target.value})}
                    className="bg-[#1a1a1a] border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-neutral-300">Slack Webhook URL</Label>
                  <Input
                    data-testid="config-slack-input"
                    type="url"
                    placeholder="https://hooks.slack.com/services/..."
                    value={configForm.slack_webhook}
                    onChange={(e) => setConfigForm({...configForm, slack_webhook: e.target.value})}
                    className="bg-[#1a1a1a] border-white/10 text-white"
                  />
                </div>
                <Button
                  data-testid="save-config-btn"
                  onClick={handleSaveConfig}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Save Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MultiChannelAlerts;
